import type {
  AppliedDiscount,
  SubscriptionResponse,
} from '../services/api'

// past_due is repaired via the Stripe portal (update the card), never a new
// checkout — a second subscription plus the dunning retry on the old open
// invoice double-charges the user (GET-8326). Keep every past-due surface
// keyed off this module so the policy and copy live in one place.
export const PAST_DUE_NOTICE
  = "Your last payment didn't go through. Update your card and you're back in business."

export function isPastDue(subscription: SubscriptionResponse | null): boolean {
  return subscription?.status === 'past_due'
}

// A seat an organization already pays for. Personal checkout is refused
// server-side for exactly this shape (billing_service.covering_organization_id
// plus active access), so keying the UI off the same facts stops us offering a
// purchase the API would 409 — and stops us hiding one it would accept.
// Role-neutral on purpose: this renders for admins as well as members, so it
// states the fact and lets the adjacent "View your team" link carry the action.
export const TEAM_COVERED_NOTICE
  = 'Your subscription is covered by your team.'

/**
 * True when this user's seat is currently billed to their organization.
 *
 * `stripe_subscription_id == null` is what separates a coverage row from a
 * member still paying their own way, and `is_active` is what keeps a lapsed
 * team trial able to subscribe personally — the org is paying nothing at that
 * point, and the server lets them through too. Deliberately narrower than
 * `billing_managed_by_team` alone, which is true for any org member (see the
 * note on the subscription endpoint) and is a presentation signal, not a
 * billing one.
 */
export function isTeamCovered(subscription: SubscriptionResponse | null): boolean {
  return Boolean(
    subscription?.billing_managed_by_team
    && subscription.stripe_subscription_id == null
    && subscription.is_active,
  )
}

/**
 * True when a promo code can be applied to this subscription in place.
 *
 * Mirrors the server's discountable_stripe_subscription_id: a personal Stripe
 * subscription (a cardless trial has none yet, a team seat has none of its
 * own) that is active or in a live trial and not winding down. past_due is
 * deliberately out — its card is fixed in the portal, and a code on an unpaid
 * invoice repairs nothing. Scheduled-to-cancel is out because the discount
 * would land on a next invoice that will never be issued.
 */
export function canApplyPromoCode(subscription: SubscriptionResponse | null): boolean {
  return Boolean(
    subscription?.stripe_subscription_id != null
    && subscription.is_active
    && (subscription.status === 'active' || subscription.status === 'trialing')
    && !subscription.scheduled_to_cancel,
  )
}

// Shown when a promo-code rejection carries no reason of its own. A billing
// failure has to say what state the plan is in, not just that it failed.
export const PROMO_FAILED_FALLBACK
  = "We couldn't apply that code — nothing has changed on your subscription. Give it another go in a moment."

function discountAmount(d: AppliedDiscount): string | null {
  if (d.percent_off != null) return `${d.percent_off}% off`
  if (d.amount_off != null) {
    const money = new Intl.NumberFormat(undefined, {
      style: 'currency', currency: (d.currency ?? 'usd').toUpperCase(),
    }).format(d.amount_off / 100)
    return `${money} off`
  }
  return null
}

/**
 * What a code bought, in the subscriber's terms: "20% off for 3 months",
 * "$50.00 off your next invoice". Stripe applies a discount from the next
 * invoice, never the one already paid, so every form names when it starts.
 */
export function describeDiscount(d: AppliedDiscount): string {
  const amount = discountAmount(d)
  if (amount == null) return 'your discount is set up, starting with your next invoice'
  if (d.duration === 'forever') return `${amount} every month, starting with your next invoice`
  if (d.duration === 'repeating' && d.duration_in_months != null) {
    const months = `${d.duration_in_months} month${d.duration_in_months === 1 ? '' : 's'}`
    return `${amount} for ${months}, starting with your next invoice`
  }
  return `${amount} your next invoice`
}

// Dunning emails deep-link to `/pricing?portal=true` so the user lands
// directly in the Stripe Customer Portal card-update flow. The user usually
// has to sign in first, which strips the query string on the OAuth
// round-trip — so the intent is stashed for the browser session and honored
// when the pricing page next mounts (mirrors the checkout intent in
// utils/referral.ts).
const PORTAL_INTENT_KEY = 'catch_portal_intent'

/** Stash `?portal=true` from the current URL, if present. */
export function capturePortalIntentFromUrl(): void {
  const params = new URLSearchParams(window.location.search)
  if (params.get('portal') === 'true') {
    sessionStorage.setItem(PORTAL_INTENT_KEY, 'true')
  }
}

export function hasPortalIntent(): boolean {
  return sessionStorage.getItem(PORTAL_INTENT_KEY) === 'true'
}

export function clearPortalIntent(): void {
  sessionStorage.removeItem(PORTAL_INTENT_KEY)
}
